import React from 'react';
import { Linking } from 'react-native';
import {
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  ButtonText,
  Badge,
  BadgeText,
  Spinner,
  ButtonIcon,
} from '@gluestack-ui/themed';
import {
  Clock, CheckCircle, Calendar, AlertCircle, ExternalLink, Check, Copy, Video,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';

import {
  formatEnglishDateLong,
  formatEnglishTime,
  formatEnglishTimezoneAbbr,
} from '@/common/utils/formatEnglishDateTime';

import { copyToClipboard } from '@/common/utils/clipboard';
import { isLiveKitBooking } from '@/common/utils/oralExam/liveKitSlot';

const ORAL_EXAM_EARLY_JOIN_MINUTES = 15;
const isActiveBookedStatus = (status) => status === 'confirmed' || status === 'pending';

const EventBookingDetails = ({
  booking,
  meetingUrl,
  canCancel,
  onCancel,
  bookingPending,
  isTaskCompleted,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const [copiedLink, setCopiedLink] = React.useState(false);

  // Oral exams join an in-app full-screen lobby; generic events keep the
  // external "Open meeting session" / "Copy link" behaviour (~90% of prod).
  const isLiveKitOralExam = isLiveKitBooking(booking);

  // Gate the Join CTA by the server-owned oral join state on the booking
  // ('upcoming' | 'open' | 'in_progress' | 'over') plus oralExamAdmitted.
  const rawOralJoinState = booking?.oralExamJoinState ?? null;
  const isOralAdmitted = Boolean(booking?.oralExamAdmitted);
  const oralExamJoinState = isLiveKitOralExam
    ? (isOralAdmitted && rawOralJoinState !== 'over' ? 'open' : (rawOralJoinState ?? 'open'))
    : null;
  const joinClosed = oralExamJoinState === 'in_progress' || oralExamJoinState === 'over';

  const handleCopyLink = () => {
    if (!meetingUrl) return;
    // Call synchronously in the gesture — do not await before clipboard write
    // (Safari/WebKit user-gesture requirement; see copyToClipboard docs).
    copyToClipboard(meetingUrl).then((ok) => {
      if (ok) setCopiedLink(true);
    });
  };

  const handleOpenMeeting = () => {
    if (!meetingUrl) return;
    // Linking.openURL works on native + web. Do NOT use window.open — on React
    // Native, `window` is often defined but `window.open` is undefined, which
    // throws TypeError: undefined is not a function (FLARE-39 / FLARE-7T).
    Linking.openURL(meetingUrl).catch(() => {});
  };

  const getStatusBadge = (status) => {
    if (joinClosed) {
      return (
        <Badge bg="$statusWarning" borderRadius="$full" size="sm" testID="oral-exam-join-closed-badge">
          <BadgeText color="$textOnWarning" size="xs">
            {t('oralExam.booking.statusJoinClosed')}
          </BadgeText>
        </Badge>
      );
    }

    if (status === 'pending') {
      return (
        <Badge bg="$success400" borderRadius="$full" size="sm">
          <BadgeText color="$white" size="xs">
            {t('student.eventTask.status.confirmed')}
          </BadgeText>
        </Badge>
      );
    }

    switch (status) {
      case 'confirmed':
        return (
          <Badge bg="$success400" borderRadius="$full" size="sm">
            <BadgeText color="$white" size="xs">
              {t('student.eventTask.status.confirmed')}
            </BadgeText>
          </Badge>
        );
      case 'completed':
        return (
          <Badge bg="$blue500" borderRadius="$full" size="sm">
            <BadgeText color="$white" size="xs">
              {t('student.eventTask.status.completed')}
            </BadgeText>
          </Badge>
        );
      default:
        return null;
    }
  };

  const daysUntilStart = dayjs(booking.startTime).diff(dayjs(), 'days');
  const hoursUntilStart = dayjs(booking.startTime).diff(dayjs(), 'hours') - daysUntilStart * 24;
  const dateLabel = formatEnglishDateLong(booking.startTime);
  const startTimeLabel = formatEnglishTime(booking.startTime);
  const endTimeLabel = formatEnglishTime(booking.endTime);
  const timezoneLabel = formatEnglishTimezoneAbbr(booking.startTime);

  return (
    <VStack space="lg">
      <HStack justifyContent="space-between" alignItems="center">
        <HStack alignItems="center" space="sm">
          <Icon as={Calendar} size="sm" color="$blue500" />
          <Text size="xs">{t('student.eventTask.registeredLabel')}</Text>
        </HStack>
        {getStatusBadge(booking.status)}
      </HStack>

      <VStack space="xs">
        <Text size="xl" fontWeight="semibold" color="$textHeading">
          {dateLabel}
        </Text>
        <Badge bg="$backgroundLight200" borderRadius="$sm" size="lg" alignSelf="flex-start" px="$2" py="$1">
          <BadgeText color="$textHeading" size="sm" fontWeight="medium">
            {startTimeLabel}
            {' '}
            -
            {endTimeLabel}
            {' '}
            {timezoneLabel}
          </BadgeText>
        </Badge>
      </VStack>

      {isActiveBookedStatus(booking.status) && !isTaskCompleted && (
        <VStack space="sm">
          {!joinClosed && (
            <HStack alignItems="flex-start" space="sm">
              <Icon as={CheckCircle} size="md" color="$green700" mt={2} />
              <Text size="sm" color="$green700" flex={1}>
                {t('student.eventTask.confirmedInstructions')}
              </Text>
            </HStack>
          )}
          {isLiveKitOralExam ? (
            oralExamJoinState === 'open' ? (
              <Button
                variant="solid"
                size="sm"
                action="primary"
                w={200}
                testID="join-oral-exam-lobby"
                onPress={() => {
                  if (!booking?.slotId) return;
                  navigation.navigate('SlotMeetingScreen', { slotId: booking.slotId });
                }}
              >
                <ButtonIcon as={Video} size="xs" mr="$1" />
                <ButtonText>{t('oralExam.joinLobby')}</ButtonText>
              </Button>
            ) : oralExamJoinState === 'upcoming' ? (
              <VStack space="xs" testID="oral-exam-join-too-early">
                <HStack alignItems="center" space="sm">
                  <Icon as={Clock} size="xs" color="$textSubtle" />
                  <Text size="sm" color="$textSubtle">
                    {t('oralExam.booking.joinOpensAt', {
                      time: formatEnglishTime(
                        dayjs(booking.startTime).subtract(ORAL_EXAM_EARLY_JOIN_MINUTES, 'minute').toDate(),
                      ),
                    })}
                  </Text>
                </HStack>
              </VStack>
            ) : oralExamJoinState === 'in_progress' ? (
              <VStack space="xs" testID="oral-exam-join-locked">
                <HStack alignItems="center" space="sm">
                  <Icon as={AlertCircle} size="xs" color="$statusWarning" />
                  <Text size="sm" color="$statusWarning" fontWeight="semibold">
                    {t('oralExam.booking.examInProgress', {
                      defaultValue: 'This session is currently in progress',
                    })}
                  </Text>
                </HStack>
                <Text size="xs" color="$textSubtle">
                  {t('oralExam.booking.examInProgressHint', {
                    defaultValue: 'The join window has closed. You do not need to contact anyone. Non-attendance will be processed after the session ends, and then you can register for another session.',
                  })}
                </Text>
              </VStack>
            ) : oralExamJoinState === 'over' ? (
              <VStack space="xs" testID="oral-exam-join-missed">
                <HStack alignItems="center" space="sm">
                  <Icon as={AlertCircle} size="xs" color="$statusWarning" />
                  <Text size="sm" color="$statusWarning" fontWeight="semibold">
                    {t('oralExam.booking.examMissed')}
                  </Text>
                </HStack>
                <Text size="xs" color="$textSubtle">
                  {t('oralExam.booking.examMissedHint')}
                </Text>
              </VStack>
            ) : null
          ) : (
            meetingUrl && (
              <HStack space="lg" alignItems="center" flexWrap="wrap">
                <Button
                  variant="link"
                  size="xs"
                  action="primary"
                  testID="open-meeting-session"
                  onPress={handleOpenMeeting}
                >
                  <ButtonIcon as={ExternalLink} size="xs" mr="$1" />
                  <ButtonText>{t('student.eventTask.openMeetingSession')}</ButtonText>
                </Button>
                <Button
                  variant="link"
                  size="xs"
                  action="secondary"
                  testID="copy-meeting-link"
                  onPress={handleCopyLink}
                >
                  <ButtonIcon as={copiedLink ? Check : Copy} size="xs" mr="$1" />
                  <ButtonText>{t('student.eventTask.copyMeetingLink')}</ButtonText>
                </Button>
              </HStack>
            )
          )}
        </VStack>
      )}

      {(booking.status === 'completed' || isTaskCompleted) && (
        <HStack alignItems="center" space="sm">
          <Icon as={CheckCircle} size="xs" color="$blue600" />
          <Text size="sm" color="$blue600">
            {t('student.eventTask.completedInstructions')}
          </Text>
        </HStack>
      )}

      {!isTaskCompleted && booking.status !== 'completed' && !joinClosed && (
        <VStack space="sm" mt="$2">
          {canCancel ? (
            <VStack space="md">
              <HStack alignItems="flex-start" space="sm">
                <Icon as={AlertCircle} size="md" color="$textSubtle" mt={2} />
                <Text size="sm" color="$textSubtle" flex={1}>
                  {t('student.eventTask.cancelRule', { days: daysUntilStart, hours: hoursUntilStart })}
                </Text>
              </HStack>
              <Button
                variant="outline"
                size="md"
                w={200}
                onPress={() => onCancel(booking.bookingId)}
                isDisabled={bookingPending}
                borderColor="$borderLight300"
              >
                {bookingPending ? (
                  <HStack alignItems="center" space="sm">
                    <Spinner size="small" />
                    <ButtonText color="$textBody">{t('student.eventTask.cancelling')}</ButtonText>
                  </HStack>
                ) : (
                  <ButtonText color="$textBody">{t('student.eventTask.cancelButton')}</ButtonText>
                )}
              </Button>
            </VStack>
          ) : (
            <HStack alignItems="center" space="sm">
              <Icon as={AlertCircle} size="xs" color="$textSubtle" />
              <Text size="xs" color="$textSubtle">
                {t('student.eventTask.cancelNotAllowed')}
              </Text>
            </HStack>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default EventBookingDetails;
